import {
  BREAKPOINTS,
  TextButton as Button,
  COLORS,
  ComponentM,
  ComponentTextStyle,
  PageWidth,
  BackButton,
  SystemIcons,
  Size
} from "@laerdal/life-react-components";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectIsAuthenticated } from "../../store/slices/userSlice";

const FullWidth = styled.div`
  width: 100%;
  border-top: 1px solid ${COLORS.neutral_200};
  background: ${COLORS.neutral_20};
  min-height: calc(100vh - 120px);
  padding: 24px 0 96px;
`;

const Header = styled.div`
    padding: 0 16px 24px;
`;

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  
  width: 100%;
  margin: 32px 0;
`;

const Card = styled.div`
  background: ${COLORS.white};
  border-radius: 12px;
  box-shadow: 0px 4px 14px rgba(0, 0, 0, 0.06);
  max-width: 360px;
  width: 100%;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;

  ${BREAKPOINTS.MEDIUM} {
    max-width: 380px;
  }
`;

const Checkmark = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 2px solid ${COLORS.positive_600};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: ${COLORS.black};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.neutral_500};
`;

const BodyText = styled(ComponentM)`
  margin: 0;
  color: ${COLORS.neutral_800};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const FullWidthButton = styled(Button)`
  width: 100%;
`;

const SignInPanel = styled.div`
  width: 100%;
  box-sizing: border-box;
  align-self: stretch;
  border: 1px solid ${COLORS.neutral_200};
  background: ${COLORS.neutral_20};
  border-radius: 8px;
  padding: 12px 16px;
  text-align: center;
  margin: 0px;
`;

const CaseConfirmationPage = () => {
  const { caseNumber } = useParams<{ caseNumber: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation("cases");
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const accountBase = process.env.REACT_APP_ACCOUNT_BASE;
  const trackUrl = accountBase
    ? `${accountBase}/cases/details/${caseNumber}`
    : "/";

  const searchParams = new URLSearchParams(window.location.search);
  const hideAccountLink = searchParams.get("hideAccountLink") === "true";

  const handleTrack = () => {
    window.open(trackUrl, "_blank");
  };

  return (
    <FullWidth>
      <PageWidth $useMaxWidth={true} $maxWidth={1024} $unsetMargin={true}>
        <Header>
          <BackButton onClick={() => window.history.back()} size={Size.Small}>
            {t("Back")}
          </BackButton>
        </Header>
        <PageContainer>
          <Card>
            <Checkmark>
              <SystemIcons.CheckMark size="24px" color={COLORS.positive_600} />
            </Checkmark>

            <Title>{t("Thank you for contacting us")}</Title>
            <Subtitle>
              {t("Case number")}: {caseNumber}
            </Subtitle>

            <BodyText textStyle={ComponentTextStyle.Regular}>
              {t(
                "Your case has been created and our team will review it shortly."
              )}
            </BodyText>

            <Actions>
              {isAuthenticated && (
                <FullWidthButton onClick={handleTrack}>
                  {t("Track this case")}
                </FullWidthButton>
              )}

              {!isAuthenticated && !hideAccountLink && (
                <SignInPanel>
                  <BodyText textStyle={ComponentTextStyle.Regular}>
                    {t(
                      "You can track the status of this case by signing in to your Laerdal account."
                    )}
                  </BodyText>
                  <div style={{ marginTop: 12 }}>
                    <FullWidthButton onClick={handleTrack}>
                      {t("Sign in")}
                    </FullWidthButton>
                  </div>
                </SignInPanel>
              )}

              <FullWidthButton
                variant="secondary"
                onClick={() => navigate("/")}>
                {t("Back to Support")}
              </FullWidthButton>
            </Actions>
          </Card>
        </PageContainer>
      </PageWidth>
    </FullWidth>
  );
};

export default CaseConfirmationPage;
